<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { useAuth } from '@/composables/useAuth'

const { startLogin, loginError } = useAuth()

const email = ref('')
const showEmailInput = ref(false)
const isRedirecting = ref(false)
const localError = ref('')
const errorMessage = computed(() => localError.value || loginError.value)

const isLoading = computed(() => isRedirecting.value)

async function showSsoEmail() {
  showEmailInput.value = true
  await nextTick()
  const input = document.getElementById('sso-email') as HTMLInputElement | null
  input?.focus()
}

function goBack() {
  showEmailInput.value = false
  email.value = ''
}

function handleLogin() {
  localError.value = ''
  isRedirecting.value = true
  try {
    startLogin(email.value)
  } catch {
    localError.value = 'Unable to connect to authentication service. Please try again.'
    isRedirecting.value = false
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') handleLogin()
}
</script>

<template>
  <div class="min-h-screen flex flex-col lg:flex-row">
    <!-- Left panel — background image with logo -->
    <div
      class="relative flex flex-col items-center justify-center lg:w-2/3 w-full lg:min-h-screen h-[20vh] bg-cover bg-center text-white"
      style="background-image: linear-gradient(0deg, rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/home-bg.jpg');"
    >
      <img
        src="/foresight-logo.png"
        alt="Foresight"
        class="h-[50px] lg:h-[60px]"
      />
      <div class="text-xs lg:text-sm text-neutral-300 mt-4 px-6 text-center">
        
      </div>
    </div>

    <div class="flex-1 flex flex-col bg-white px-8 py-12 lg:py-0">
      <div class="flex-1 flex items-center justify-center">
        <div class="w-full max-w-[400px] text-center lg:text-left">
          <h2 class="text-xl font-bold text-neutral-800 mb-2">
            FORESIGHT Reports : Smart Assist
          </h2>
          <p class="text-sm text-neutral-500 mb-6">
            Turn FORESIGHT Reports' activism and protest data into decision-ready intelligence. 
          </p>

          <p v-if="errorMessage" class="text-sm text-red-600 mb-4">
            {{ errorMessage }}
          </p>

          <div class="mb-4">
            <Transition name="expand">
              <div v-if="showEmailInput" class="mb-3">
                <input
                  id="sso-email"
                  v-model="email"
                  type="email"
                  class="w-full px-3 py-2.5 bg-[#fafafa] border border-neutral-300 rounded-md text-neutral-800 text-sm focus:outline-none focus:border-blue-500 placeholder-neutral-400"
                  placeholder="Email address"
                  :disabled="isLoading"
                  @keydown="onKeydown"
                />
                <p class="text-xs text-neutral-400 mt-1.5">
                  Enter your work email to be directed to your organization's sign-in page.
                </p>
              </div>
            </Transition>

            <div class="flex items-center gap-3">
              <button
                class="px-6 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isLoading"
                @click="handleLogin"
              >
                <span v-if="isLoading" class="flex items-center gap-2">
                  <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in…
                </span>
                <span v-else>Sign in</span>
              </button>

              <button
                v-if="!showEmailInput"
                class="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                @click="showSsoEmail"
              >
                SSO user? Enter email
              </button>

              <button
                v-else
                class="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                :disabled="isLoading"
                @click="goBack"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="pt-8 pb-4 text-center">
        <p class="text-xs text-neutral-400">
          &copy; {{ new Date().getFullYear() }} FORESIGHT Reports
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}
.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-bottom: 0;
}
.expand-enter-to,
.expand-leave-from {
  max-height: 120px;
  opacity: 1;
}
</style>
